import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as api from '../data/account'
import { useAccount } from './account-context'

/* What the account pages ask the API for.

   Every key starts with 'account', which is what lets the provider drop all
   of it in one call when somebody signs out: these rows are one person's
   bookings and telephone number, and they must not be sitting in the cache
   when the next person signs in on the same machine.

   Nothing here is held for long. The catalogue is fresh for five minutes
   because it changes a few times a week; a buyer's own balance changes the
   moment the desk records a payment, and somebody opening this page is
   usually opening it to check exactly that. */

const FRESH_FOR = 30_000

export const accountKeys = {
  me: () => ['account', 'me'] as const,
  bookings: () => ['account', 'bookings'] as const,
  vehicles: () => ['account', 'vehicles'] as const,
  instalments: () => ['account', 'instalments'] as const,
}

/** True once there is a session that may actually read these. Every query
    below waits on it, so a signed-out visitor fires no requests at all. */
function useEnabled() {
  const { status, can } = useAccount()
  return status === 'signed-in' && can('account.view')
}

export function useAccountQuery() {
  return useQuery({
    queryKey: accountKeys.me(),
    queryFn: api.getAccount,
    enabled: useEnabled(),
    staleTime: FRESH_FOR,
  })
}

export function useMyBookings() {
  return useQuery({
    queryKey: accountKeys.bookings(),
    queryFn: api.getMyBookings,
    enabled: useEnabled(),
    staleTime: FRESH_FOR,
  })
}

export function useMyVehicles() {
  return useQuery({
    queryKey: accountKeys.vehicles(),
    queryFn: api.getMyVehicles,
    enabled: useEnabled(),
    staleTime: FRESH_FOR,
  })
}

export function useMyInstalments() {
  return useQuery({
    queryKey: accountKeys.instalments(),
    queryFn: api.getMyInstalments,
    enabled: useEnabled(),
    staleTime: FRESH_FOR,
  })
}

/** Correcting your own contact details. The summary is invalidated with it:
    the account page prints the number it just changed. */
export function useUpdateAccount() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (changes: api.AccountChanges) => api.updateAccount(changes),
    onSuccess: () => client.invalidateQueries({ queryKey: accountKeys.me() }),
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; password: string }) =>
      api.changePassword(input.currentPassword, input.password),
    /* Never retried: a timed-out change may well have landed, and the second
       attempt would fail on a current password that is no longer current. */
    retry: false,
  })
}
